import {
    ActivityCategory,
    activityCategoryLabels,
    activityCategoryLogLabels,
    ActivityTargetView,
    categoryActivityTarget,
    genericLogLabel,
    serializeActivityTarget,
    taskTypeActivityTarget,
    TaskTypeId,
} from "@sp-crm/core";
import { ActivityTypeOption } from "components/tasks/activity-type-options";

export interface AgendaDueRow {
    touchpointId: string;
    contactId: string;
    contactName: string;
    orgName?: string | null;
    contactEmail?: string | null;
    contactPhone?: string | null;
    contactEmailOptedOut: boolean;
    tierListItemId: string;
    tierName?: string | null;
    tierColor?: string | null;
    targets: ActivityTargetView[];
    intervalCount: number;
    intervalUnit: string;
    lastContactedAt?: string | null;
}

export interface AgendaTouchpoint {
    touchpointId: string;
    targets: ActivityTargetView[];
    intervalCount: number;
    intervalUnit: string;
    lastContactedAt: string | null;
}

export const targetCategories = (targets: ActivityTargetView[]): Set<ActivityCategory> =>
    new Set(targets.map(target => target.category));

export interface LoggedActivityChoice {
    key: string;
    label: string;
    category: ActivityCategory;
    taskTypeId: TaskTypeId | null;
}

export interface LoggedActivityGroup {
    category: ActivityCategory;
    label: string;
    choices: LoggedActivityChoice[];
}

const typeChoice = (option: ActivityTypeOption): LoggedActivityChoice => ({
    key: serializeActivityTarget(taskTypeActivityTarget(option.id)),
    label: option.name,
    category: option.category,
    taskTypeId: option.id,
});

const wholeCategoryChoice = (category: ActivityCategory): LoggedActivityChoice => ({
    key: serializeActivityTarget(categoryActivityTarget(category)),
    label: activityCategoryLabels[category],
    category,
    taskTypeId: null,
});

export const activityChoicesForCategory = (
    targets: ActivityTargetView[],
    category: ActivityCategory,
    activityTypes: ActivityTypeOption[],
): LoggedActivityChoice[] => {
    const inCategory = targets.filter(target => target.category === category);
    const scopedTypeIds = inCategory.flatMap(target =>
        target.kind === "taskType" ? [target.taskTypeId] : [],
    );
    if (!inCategory.some(target => target.kind === "category")) {
        return activityTypes
            .filter(option => scopedTypeIds.includes(option.id))
            .map(typeChoice);
    }
    const offerable = activityTypes.filter(
        option =>
            option.category === category &&
            (!option.isArchived || scopedTypeIds.includes(option.id)),
    );
    return offerable.length > 0
        ? offerable.map(typeChoice)
        : [wholeCategoryChoice(category)];
};

export const loggedActivityGroups = (
    targets: ActivityTargetView[],
    activityTypes: ActivityTypeOption[],
): LoggedActivityGroup[] => {
    const categories: ActivityCategory[] = [];
    for (const target of targets) {
        if (!categories.includes(target.category)) {
            categories.push(target.category);
        }
    }
    return categories.map(category => ({
        category,
        label: activityCategoryLabels[category],
        choices: activityChoicesForCategory(targets, category, activityTypes),
    }));
};

export const loggedActivityChoices = (
    groups: LoggedActivityGroup[],
): LoggedActivityChoice[] => groups.flatMap(group => group.choices);

export interface AgendaContactGroup {
    contactId: string;
    contactName: string;
    orgName: string | null;
    contactEmail: string | null;
    contactPhone: string | null;
    contactEmailOptedOut: boolean;
    tierListItemId: string;
    tierName: string | null;
    tierColor: string | null;
    touchpoints: AgendaTouchpoint[];
}

export const groupDueTouchpoints = (rows: AgendaDueRow[]): AgendaContactGroup[] => {
    const groups: AgendaContactGroup[] = [];
    const byContact = new Map<string, AgendaContactGroup>();
    for (const row of rows) {
        const existing = byContact.get(row.contactId);
        const group = existing ?? createGroup(row);
        if (!existing) {
            byContact.set(row.contactId, group);
            groups.push(group);
        }
        group.touchpoints.push({
            touchpointId: row.touchpointId,
            targets: row.targets,
            intervalCount: row.intervalCount,
            intervalUnit: row.intervalUnit,
            lastContactedAt: row.lastContactedAt ?? null,
        });
    }
    return groups;
};

const createGroup = (row: AgendaDueRow): AgendaContactGroup => ({
    contactId: row.contactId,
    contactName: row.contactName,
    orgName: row.orgName ?? null,
    contactEmail: row.contactEmail ?? null,
    contactPhone: row.contactPhone ?? null,
    contactEmailOptedOut: row.contactEmailOptedOut,
    tierListItemId: row.tierListItemId,
    tierName: row.tierName ?? null,
    tierColor: row.tierColor ?? null,
    touchpoints: [],
});

export const filterGroupsByTier = (
    groups: AgendaContactGroup[],
    selectedTierIds: string[],
): AgendaContactGroup[] => {
    if (selectedTierIds.length === 0) {
        return groups;
    }
    const selected = new Set(selectedTierIds);
    return groups.filter(group => selected.has(group.tierListItemId));
};

export interface AgendaSnoozeRef {
    contactId: string;
    touchpointId: string | null;
}

export interface AgendaCompletedTouchpoint {
    touchpointId: string;
    contactId: string;
    contactName: string;
    orgName: string | null;
    tierName: string | null;
    tierColor: string | null;
    targets: ActivityTargetView[];
    scheduledVisit: boolean;
    completedAt: string;
}

export const logLabelForCategory = (category: ActivityCategory | null): string =>
    category ? activityCategoryLogLabels[category] : genericLogLabel;

export const completedTypeName = (targets: ActivityTargetView[]): string | null => {
    const named = targets.filter(target => target.kind === "taskType");
    return named.length === 1 ? named[0].label : null;
};

export const formatCompletedAt = (completedAt: string): string => {
    const when = new Date(completedAt);
    const day = when.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    const time = when.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
    });
    return `${day}, ${time}`;
};

export const dueRowKey = (row: { contactId: string; touchpointId: string }): string =>
    `${row.contactId}|${row.touchpointId}`;

export const computeNewlyCompleted = (
    previousRows: AgendaDueRow[],
    currentRows: AgendaDueRow[],
    currentSnoozes: AgendaSnoozeRef[],
    contactsWithUpcomingVisit: Set<string>,
    now: Date,
): AgendaCompletedTouchpoint[] => {
    const stillDue = new Set(currentRows.map(dueRowKey));
    const snoozedContacts = new Set(
        currentSnoozes
            .filter(snooze => snooze.touchpointId === null)
            .map(snooze => snooze.contactId),
    );
    const snoozedTouchpoints = new Set(
        currentSnoozes
            .filter(
                (snooze): snooze is { contactId: string; touchpointId: string } =>
                    snooze.touchpointId !== null,
            )
            .map(dueRowKey),
    );

    const completed = previousRows
        .filter(row => !stillDue.has(dueRowKey(row)))
        .filter(row => !snoozedContacts.has(row.contactId))
        .filter(row => !snoozedTouchpoints.has(dueRowKey(row)))
        .map(row => ({
            touchpointId: row.touchpointId,
            contactId: row.contactId,
            contactName: row.contactName,
            orgName: row.orgName ?? null,
            tierName: row.tierName ?? null,
            tierColor: row.tierColor ?? null,
            targets: row.targets,
            scheduledVisit:
                targetCategories(row.targets).has("visit") &&
                contactsWithUpcomingVisit.has(row.contactId),
            completedAt: now.toISOString(),
        }));

    return collapseScheduledVisits(completed);
};

const collapseScheduledVisits = (
    completed: AgendaCompletedTouchpoint[],
): AgendaCompletedTouchpoint[] => {
    const contactsAlreadyScheduled = new Set<string>();
    return completed.filter(item => {
        if (!item.scheduledVisit) {
            return true;
        }
        if (contactsAlreadyScheduled.has(item.contactId)) {
            return false;
        }
        contactsAlreadyScheduled.add(item.contactId);
        return true;
    });
};

export type EmailActionState = "send-email" | "add-email" | "email-opted-out";

export const resolveEmailAction = (input: {
    contactEmail: string | null;
    contactEmailOptedOut: boolean;
}): EmailActionState => {
    if (input.contactEmailOptedOut) {
        return "email-opted-out";
    }
    if (input.contactEmail) {
        return "send-email";
    }
    return "add-email";
};

export interface TouchpointActions {
    email: EmailActionState | null;
    addContactPhone: boolean;
    provisionPhoneLine: boolean;
    call: boolean;
    text: boolean;
    visit: boolean;
}

export const resolveTouchpointActions = (input: {
    targets: ActivityTargetView[];
    contactEmail: string | null;
    contactEmailOptedOut: boolean;
    hasContactPhone: boolean;
    hasPhoneLine: boolean;
}): TouchpointActions => {
    const categories = targetCategories(input.targets);
    const wantsCall = categories.has("call");
    const wantsText = categories.has("sms");
    const wantsPhone = wantsCall || wantsText;
    const callable = wantsPhone && input.hasContactPhone && input.hasPhoneLine;
    return {
        email: categories.has("email") ? resolveEmailAction(input) : null,
        addContactPhone: wantsPhone && !input.hasContactPhone,
        provisionPhoneLine: wantsPhone && input.hasContactPhone && !input.hasPhoneLine,
        call: wantsCall && callable,
        text: wantsText && callable,
        visit: categories.has("visit"),
    };
};

export const agendaNeedsPhoneProvisioning = (
    groups: AgendaContactGroup[],
    hasPhoneLine: boolean,
): boolean => {
    if (hasPhoneLine) {
        return false;
    }
    return groups.some(group =>
        group.touchpoints.some(touchpoint => {
            const categories = targetCategories(touchpoint.targets);
            return categories.has("call") || categories.has("sms");
        }),
    );
};
